import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Key, Plus, Copy, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SCOPES = ["read:jobs", "write:jobs", "read:invoices", "read:claims", "read:customers"];

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", scopes: ["read:jobs"] });
  const [newKey, setNewKey] = useState(null);
  const load = () => api.get("/admin/api-keys").then((r) => setKeys(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name) return toast.error("Name required");
    const r = await api.post("/admin/api-keys", form);
    setNewKey(r.data);
    setForm({ name: "", scopes: ["read:jobs"] });
    setOpen(false); load();
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Apps using it will lose access immediately.")) return;
    await api.delete(`/admin/api-keys/${id}`); toast.success("Revoked"); load();
  };
  return (
    <div className="space-y-6 animate-slide-up" data-testid="api-keys-page">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-600/15 border border-blue-500/20 flex items-center justify-center"><Key className="w-6 h-6 text-blue-400" /></div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Admin · Integrations</div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">API Keys (Public API v1)</h1>
            <p className="text-sm text-slate-400 mt-1">Authenticate Zapier / QuickBooks / partner integrations · scoped per key</p>
          </div>
        </div>
        <Button data-testid="create-key-btn" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-500"><Plus className="w-4 h-4 mr-2" />New Key</Button>
      </div>

      {newKey && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="new-key-banner">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-amber-400" /><div className="text-sm font-semibold text-amber-300">Copy this key now — it won't be shown again</div></div>
          <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3 font-mono text-xs text-blue-300 break-all">{newKey.key}</div>
          <Button data-testid="copy-new-key-btn" size="sm" onClick={() => { navigator.clipboard.writeText(newKey.key); toast.success("Copied"); }} className="mt-2 bg-amber-500 hover:bg-amber-400 text-slate-900"><Copy className="w-3 h-3 mr-1.5" />Copy</Button>
          <button onClick={() => setNewKey(null)} className="text-xs text-slate-400 ml-3">Dismiss</button>
        </div>
      )}

      <div className="space-y-2">
        {keys.length === 0 ? <div className="text-center text-slate-500 py-12 rounded-xl border border-slate-800 bg-[#0B1120]">No API keys yet</div> :
          keys.map((k) => (
            <div key={k.id} data-testid={`key-${k.id}`} className="rounded-xl border border-slate-800 bg-[#0B1120] p-4 flex items-center gap-4">
              <Key className="w-4 h-4 text-blue-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{k.name} {!k.active && <Badge className="bg-red-500/15 text-red-300 ml-2">REVOKED</Badge>}</div>
                <div className="text-[10px] font-mono text-slate-500 mt-0.5">{k.key_preview}</div>
                <div className="flex gap-1 mt-1">{(k.scopes || []).map((s) => <Badge key={s} className="bg-slate-800 border-slate-700 text-slate-400 text-[9px]">{s}</Badge>)}</div>
              </div>
              <div className="text-right text-[10px] text-slate-500">
                <div>{k.usage_count || 0} calls</div>
                {k.last_used_at && <div>last: {new Date(k.last_used_at).toLocaleDateString()}</div>}
              </div>
              {k.active && <button data-testid={`revoke-${k.id}`} onClick={() => revoke(k.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Quick Reference</div>
        <pre className="text-xs text-slate-300 bg-slate-900/60 rounded-lg p-3 overflow-x-auto"><code>{`# List jobs
curl https://YOUR-DOMAIN/api/v1/jobs \\
  -H "Authorization: Bearer tlk_your_key_here"

# List invoices
curl https://YOUR-DOMAIN/api/v1/invoices \\
  -H "Authorization: Bearer tlk_your_key_here"`}</code></pre>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B1120] border-slate-800">
          <DialogHeader><DialogTitle>Create API Key</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="key-name" placeholder="Friendly name (e.g. QuickBooks Production)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#030712] border-slate-800" />
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Scopes</div>
              <div className="space-y-1">
                {SCOPES.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm">
                    <input data-testid={`scope-${s}`} type="checkbox" checked={form.scopes.includes(s)} onChange={(e) => {
                      const next = e.target.checked ? [...form.scopes, s] : form.scopes.filter((x) => x !== s);
                      setForm({ ...form, scopes: next });
                    }} className="w-4 h-4" />
                    <code className="text-xs text-blue-300">{s}</code>
                  </label>
                ))}
              </div>
            </div>
            <Button data-testid="create-submit-btn" onClick={create} className="w-full bg-blue-600 hover:bg-blue-500">Create</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
